import { useMemo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';

import ComponentRenderer from '../../ComponentRenderer';
import { parseJSONSchema } from '../../utils/data-parser';
import { PageProvider, PageMode } from '../../context/PageContext';
import { AntIcon } from '../../../components';
import type { IComponentSchema } from '../../types';
import { toRuntimeTemplates, type FlowNodeType } from './transform';

const FlowNode = ({ data, selected }: NodeProps<FlowNodeType>) => {
  // Scope shared by the card's static bindings ($node/$row/$sides) and — via the
  // PageProvider below — by any `Loop` inside the card (which resolves its own
  // `items` against page.context, not the parse scope).
  const scope = useMemo(
    () => ({
      row: data.item,
      sides: data.sides,
      node: {
        label: data.label,
        subLabel: data.subLabel,
        category: data.category,
        code: data.code,
        groupCodes: data.groupCodes,
        color: data.color,
        icon: data.icon,
        role: data.role,
        sides: data.sides,
        resources: data.resources,
      },
    }),
    [
      data.item,
      data.label,
      data.subLabel,
      data.category,
      data.code,
      data.groupCodes,
      data.color,
      data.icon,
      data.role,
      data.sides,
      data.resources,
    ]
  );
  const parsedCard = useMemo(
    () =>
      data.cardSchema
        ? (parseJSONSchema(
            toRuntimeTemplates(data.cardSchema),
            scope
          ) as IComponentSchema<Record<string, unknown>>)
        : null,
    [data.cardSchema, scope]
  );

  return (
    <div
      style={{
        position: 'relative',
        minWidth: 220,
        background: '#ffffff',
        borderRadius: 12,
        borderLeft: `4px solid ${data.color}`,
        boxShadow: selected
          ? `0 0 0 2px ${data.color}`
          : '0 1px 2px rgba(15,23,42,0.12)',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      {data.invalid && (
        <span
          title={
            data.issues?.length
              ? data.issues.join('\n')
              : 'Missing required fields'
          }
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#e11d48',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '18px',
            textAlign: 'center',
            boxShadow: '0 1px 2px rgba(15,23,42,0.25)',
          }}
        >
          !
        </span>
      )}
      <Handle type="target" position={Position.Top} />
      {/* A custom card owns its whole layout (icon included) — only show the
          built-in leading icon in the default layout, or it renders twice. */}
      {!parsedCard && data.icon && (
        <span style={{ fontSize: 18, color: data.color, lineHeight: 1 }}>
          <AntIcon name={data.icon} />
        </span>
      )}

      {parsedCard ? (
        <div style={{ minWidth: 0 }}>
          <PageProvider value={{ context: scope, mode: PageMode.WORK }}>
            <ComponentRenderer schema={parsedCard} />
          </PageProvider>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span
            style={{
              fontSize: 10,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: '#94a3b8',
            }}
          >
            {data.category}
          </span>
          <strong style={{ fontSize: 14, color: '#0f172a' }}>
            {data.label}
          </strong>
          {data.subLabel && (
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {data.subLabel}
            </span>
          )}
        </div>
      )}

      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export default FlowNode;
